/**
 * ImagesContext - React Context for managing generated images.
 *
 * Provides:
 * - Generated images array
 * - Images loading state
 * - Actions for requesting iterations and loading images
 *
 * Part of the granular context split for performance optimisation.
 * Components that only need images won't re-render when
 * session metadata or transcript change.
 */

/* eslint-disable react-refresh/only-export-components -- Context exports hook alongside provider */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'
import type { GeneratedImage } from '@/models'
import { getApiClient, type RequestImageIterationRequest } from '@/services/api'

/**
 * Images state
 */
export interface ImagesState {
  images: GeneratedImage[]
  isLoading: boolean
  error: string | null
}

/**
 * Images actions
 */
export interface ImagesActions {
  requestImageIteration: (sessionId: string, request: RequestImageIterationRequest) => Promise<GeneratedImage>
  loadImages: (sessionId: string) => Promise<GeneratedImage[]>
  clearImages: () => void
  setImages: (images: GeneratedImage[]) => void
  addImage: (image: GeneratedImage) => void
}

/**
 * Images context value
 */
export interface ImagesContextValue extends ImagesState, ImagesActions {}

const ImagesContext = createContext<ImagesContextValue | undefined>(undefined)

/**
 * Images provider props
 */
export interface ImagesProviderProps {
  children: ReactNode
}

/**
 * Images provider component
 *
 * Manages only the generated images array and its loading/error states.
 * Separated from session data and transcript for granular re-renders.
 */
export function ImagesProvider({ children }: ImagesProviderProps) {
  const [state, setState] = useState<ImagesState>({
    images: [],
    isLoading: false,
    error: null,
  })

  const apiClient = getApiClient()

  /**
   * Request image iteration (refinement)
   */
  const requestImageIteration = useCallback(async (
    sessionId: string,
    request: RequestImageIterationRequest
  ): Promise<GeneratedImage> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const newImage = await apiClient.requestImageIteration(sessionId, request)

      setState(prev => ({
        ...prev,
        images: [...prev.images, newImage],
        isLoading: false,
      }))

      return newImage
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to request iteration'
      console.error('[ImagesContext] requestImageIteration failed', {
        sessionId,
        error: error instanceof Error ? error.stack : error,
      })
      setState(prev => ({ ...prev, isLoading: false, error: message }))
      throw error
    }
  }, [apiClient])

  /**
   * Load images for a session
   */
  const loadImages = useCallback(async (sessionId: string): Promise<GeneratedImage[]> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const images = await apiClient.getImages(sessionId)
      setState({ images, isLoading: false, error: null })
      return images
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load images'
      console.error('[ImagesContext] loadImages failed', {
        sessionId,
        error: error instanceof Error ? error.stack : error,
      })
      setState(prev => ({ ...prev, isLoading: false, error: message }))
      throw error
    }
  }, [apiClient])

  /**
   * Clear images state
   */
  const clearImages = useCallback(() => {
    setState({ images: [], isLoading: false, error: null })
  }, [])

  /**
   * Set images directly (for hydration/sync)
   */
  const setImages = useCallback((images: GeneratedImage[]) => {
    setState(prev => ({ ...prev, images }))
  }, [])

  /**
   * Add a single image (for optimistic updates)
   */
  const addImage = useCallback((image: GeneratedImage) => {
    setState(prev => ({ ...prev, images: [...prev.images, image] }))
  }, [])

  const value = useMemo(() => ({
    ...state,
    requestImageIteration,
    loadImages,
    clearImages,
    setImages,
    addImage,
  }), [state, requestImageIteration, loadImages, clearImages, setImages, addImage])

  return (
    <ImagesContext.Provider value={value}>
      {children}
    </ImagesContext.Provider>
  )
}

/**
 * Hook to use images context
 */
export function useImages(): ImagesContextValue {
  const context = useContext(ImagesContext)

  if (!context) {
    throw new Error('useImages must be used within ImagesProvider')
  }

  return context
}
